import clsx from "clsx";
import React from "react";
import { LoadingBox } from "src/ui/shared/Loading";
import { makeStyles } from "tss-react/mui";

import { Box } from "@mui/material";

const useStyles = makeStyles<{ maxWidth: number | "unset"; scrollable: boolean }>()((theme, { maxWidth, scrollable }) => ({
    root: {
        width: "100%",
        minHeight: "100vh",
        overflow: scrollable ? "auto" : "hidden",
        flexGrow: 1,
        backgroundColor: theme.palette.emteria.background.default,
    },
    fitViewport: {
        height: "100vh",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
    },
    compact: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
    },
    content: {
        maxWidth,
        padding: theme.spacing(8, 8, 4, 8),
        width: "100%",
        position: "relative",

        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(2, 2),
        },
    },
}));

/**
 * @param fitViewport - Whether to fit the container to the viewport height.
 * @param isLoading - Whether to show a loading indicator.
 * @param fullWidth - Whether to use the full width of the container.
 * @param scrollable - Scroll inside the container when the content is taller than the viewport.
 */
type PageContainerProps = React.ComponentProps<"div"> & {
    fitViewport?: boolean;
    isLoading?: boolean;
    fullWidth?: boolean;
    scrollable?: boolean;
};

export const PageContainer = ({
    className,
    fitViewport = false,
    fullWidth = true,
    scrollable = false,
    children,
    isLoading,
    ...rest
}: PageContainerProps) => {
    const { classes } = useStyles({ maxWidth: fullWidth ? "unset" : 1024, scrollable });

    if (isLoading) {
        return (
            <Box {...rest} className={clsx(classes.root, classes.fitViewport, classes.compact)}>
                <LoadingBox />
            </Box>
        );
    }

    return (
        <Box {...rest} className={clsx(classes.root, !fullWidth && classes.compact)}>
            <Box className={clsx(fitViewport && classes.fitViewport, classes.content, className)}>{children}</Box>
        </Box>
    );
};
